import { convertChannelCasing } from 'lib-shared-api-utils';
import type { ConversationsChannels } from 'lib-shared-legacy-types';
import type { Channel } from 'lib-shared-types-channels';
import { useCallback, useMemo } from 'react';

import {
  type ConversationSummaryVisibilityState,
  summaryVisibilityStateDefault,
} from 'apis/userStorage';
import { getChannelMedia } from 'utils/channels';
import { useServerStorage } from 'utils/useServerStorage';

/**
 * Used for limiting the number of stored "csids" to prevent storage pollution
 * by eliminating ones that haven't been accessed for an extended period. This
 * optimization ensures the storage remains efficient.
 */
const maxVisibilityOverrides = 100;

export type SummaryVisibilityValues = {
  /** Whether the summary of this conversation should be visible. */
  readonly isSummaryVisible?: boolean;
  /** Set current conversation's summary's visibility. */
  readonly setIsSummaryVisible?: (isSummaryVisible: boolean) => void;
};

const getDefaultVisibility = (
  channel: ConversationsChannels | null | undefined,
  summaryVisibilityState: ConversationSummaryVisibilityState,
): boolean => {
  try {
    // Channel might be null under some circumstances
    const media =
      typeof channel === 'string'
        ? getChannelMedia(convertChannelCasing(channel, 'snake') as Channel)
        : null;
    if (media !== null && media in summaryVisibilityState) {
      return summaryVisibilityState[media] === 'auto';
    }
  } catch (error) {}

  // If we can't determine the default visibility, we default to false/on-demand
  return false;
};

const visibilityDefaults = {
  csidsSummaryVisibilityOverride: [],
  summaryVisibilityState: summaryVisibilityStateDefault,
};

export const useConversationSummaryVisibility = (
  csid: number,
  channel?: ConversationsChannels | null,
): SummaryVisibilityValues => {
  const { data, set } = useServerStorage('user');
  const { summaryVisibilityState, csidsSummaryVisibilityOverride } = {
    ...visibilityDefaults,
    ...data['agent_copilot'],
    summaryVisibilityState: {
      ...visibilityDefaults.summaryVisibilityState,
      ...(data['agent_copilot']?.summaryVisibilityState || {}),
    } as ConversationSummaryVisibilityState,
  };

  const isVisibleByDefault = getDefaultVisibility(
    channel,
    summaryVisibilityState,
  );

  const isSummaryVisible = useMemo(() => {
    const csidOverride = csidsSummaryVisibilityOverride.find(
      (override) => override.csid === csid,
    );
    if (csidOverride) {
      return csidOverride.visible;
    }
    return isVisibleByDefault;
  }, [csid, csidsSummaryVisibilityOverride, isVisibleByDefault]);

  const setIsSummaryVisible = useCallback(
    (isVisible) => {
      set({
        agent_copilot: (draft) => {
          if (csid === null) return;
          const currentVisibility = draft.csidsSummaryVisibilityOverride ?? [];
          draft.csidsSummaryVisibilityOverride = currentVisibility.filter(
            (override) => override.csid !== csid,
          );

          draft.csidsSummaryVisibilityOverride = [
            // insert new entry at beginning of array so it is the first to be
            // pruned when the array gets too large
            {
              csid,
              visible: isVisible,
            },
            ...draft.csidsSummaryVisibilityOverride,
          ]
            // prune old entries
            .slice(0, maxVisibilityOverrides);
        },
      });
    },
    [csid, set],
  );

  return useMemo(
    () => ({
      isSummaryVisible,
      setIsSummaryVisible,
    }),
    [isSummaryVisible, setIsSummaryVisible],
  );
};
